import { Ref } from 'react';

import { PlayerApiError } from '@/scenarios/legacy-player/types/error';
import { Stream } from '@/scenarios/legacy-player/types/config';

enum MonetizationModel {
    EST = 'EST',
    TVOD = 'TVOD',
    SVOD = 'SVOD',
    FREE = 'FREE',
    AVOD = 'AVOD',
}

enum ContentTypeId {
    TV_SERIES = 6,
    MOVIE = 20,
    OTT_EPISODE = 21,
    OTT_TRAILER = 37,
}

export interface PlayerProps {
    withoutSkin?: boolean;
    source?: SourceConfig;
    filmId?: string;
    profileId?: number;
    contentId?: string;
    forceSubtitles?: boolean;
    /** Default: false */
    forceDefaultSkin?: boolean;
    /** Default: true */
    ignoreStartPositionOnSourceChange?: boolean;
    /** Defalt: true */
    showAgeRestriction?: boolean;
    isPictureInPicture?: boolean;
    playerType?: 'promo' | 'trailer' | 'film' | 'tvSeries' | 'channel' | 'old' | 'sport';
    telemetryContentPlace: TelemetryContentPlace;
    telemetryContentType: TelemetryContentType;

    rootRef?: Ref<HTMLDivElement>;

    onSourceChange?: (newSource: any) => void;
    onContentReady?: () => void;
    onInitStart?: () => void;
    onStart?: (contentId: string) => void;
    onFinish?: (contentId: string) => void;
    onError?: (contentId: string, error: PlayerApiError) => void;
    onBeforeInit?: () => void;
    onCurrentTimeChange?: (time: number) => void;
    onDurationChange?: (duration: number) => void;
    onWatchedTimeChange?: (watchedTime: number) => void;
    onSelectEpisode?: () => void;
    onCreate?: () => void;
    onReady?: () => void;
    onDestroy?: () => void;

    onClose?: () => void;
}

export interface PublicYangoMoviesTrack {
    /** Track name, displayed in the interface */
    name: string;
    /** Track ID, used for switching tracks via API or selector in the interface */
    value: string;
    lang: string;
}

enum ContentLanguage {
    ENG = 'eng',
    RU = 'ru',
    RUS = 'rus',
}

export const PrimaryLanguage = ContentLanguage.RU;

interface ExternalPlayerConfig {
    provider_item_id: string;
    player_id: string;
}

interface VideoInfo {
    videoContentId?: string;
    videoContentName?: string;
    videoPublisherId?: string;
    videoPublisherName?: string;
    videoGenreId?: string;
    videoGenreName?: string;
}

export interface SubtitleConfig {
    title: string;
    language: string;
    languageName?: string;
    forAdult?: boolean;
    studio?: string;
    type?: string;
    url: string;
    visibleByDefault: boolean;
    index: number;
}

export interface AudioConfig {
    audioGroupId: number;
    audioChannelsNumber: number;
    isForAdult: boolean;
    title?: string;
    language: string;
    languageName?: string;
    quality?: string;
    studioName?: string;
    visibleByDefault: boolean;
    index: number;
}

export type SubtitlesConfig = SubtitleConfig[];

export type AudiosConfig = AudioConfig[];

export interface TrackingEvents {
    watchedTime?: (string | { offset: number })[][];
}

interface ConcurrencyArbiterConfig {
    server: string;
    requestParams: {
        [key: string]: any;
    };
}

interface SourceConfig {
    streams: Stream[];
    id?: string | number;
    /**
     * конфиг для конкурентных просмотров. Используется в player-wrapper
     */
    concurrencyArbiter?: ConcurrencyArbiterConfig & {
        heartbeatDelta?: number;
        enforcedHeartbeat?: number;
    };
    /**
     * время в секундах с какого момента начинать воспроизведение
     */
    startPosition?: number;
    /**
     * воспроизводить видео в зацикленном режиме
     */
    loop?: boolean;
    /**
     * Используется для авторизации по cookies
     */
    withCredentials?: boolean;
    /**
     * Показ превью
     */
    preview?: string;
    /**
     * Показ превью
     */
    additionalParams?: {
        /** Название сервиса */
        from: string;
        /** Тип контента */
        from_block?: string;
        /** Паспортный uid */
        puid?: string | undefined;
        inband_events?: 0 | 1; // STRM-4046
        slots?: string;
        reqid?: string | null;
    };
    /**
     * Конфиг для выноса воспроизведения на станцию
     */
    externalPlayerConfig?: ExternalPlayerConfig;
    /**
     * PlayerContext необходимый для player-wrapper.
     */
    context?: PlayerContext | null;
    /**
     * Идентификация контента. ID в CMS, Жанр, ID правообладателя и т.д.
     */
    videoInfo?: VideoInfo;
    /**
     * Субтитры
     */
    subtitles?: SubtitlesConfig;
    /**
     * Аудиодорожки
     */
    audio?: AudiosConfig;
    /**
     * Описание типа поля для трекингов в конфигурации плеера.
     */
    trackings?: {
        trackingEvents?: TrackingEvents;
    };
    /**
     * player-wrapper: > 4.12.0 Описание типа поля для трекингов в конфигурации плеера.
     */
    initialTrackings?: {
        trackingEvents?: TrackingEvents;
    };
    /**
     * Языковые коды дорожек аудио и субтитров в порядке приоритета
     */
    preferredTracks?: {
        preferredAudioLangs: string[];
        preferredTextLangs: string[];
    };
    meta?: {
        startFetchingTime?: number;
    };
}

type PlayerContext = {
    yandexuid: string | undefined;
    from: string;
    serviceName: string;
    contentTypeId: string | ContentTypeId | null;
    sid?: string;
    uuid?: string;
    serialContentGroupUuid?: string;
    kpId?: string;
    page?: string;
    monetizationModel?: MonetizationModel;
    testIds?: string;
    eventId?: string;
    pathId?: string;
    puid?: string;
    position?: number;
    psid?: string;
    /** 1 - desktop браузер for Mediascope */
    deviceType?: 1;
    browserName?: string;
    browserVer?: string;
    browserVerMajor?: string;
    browserVerMinor?: string;
    deviceUuid?: string;
    prepareStartupDataTime?: number;
    isMultiplexChannel?: boolean;
    vitrinaTvChannelSlug?: string;
    platformOs?: string;
    platformOsVersionMajor?: string;
    platformOsVersionMinor?: string;
    contentPackage?: string;
    billingFeatureNames?: string;
    deviceId?: string;
    appVersion?: string;
    platformVersion?: string;
    platformModel?: string;
    platformVendor?: string;
    platformVendorDeviceId?: string;
    prerollUuid?: string;
};

/**
 * Набор значений для параметра from_block для плеера.
 * Значения описывают возможные места в приложении,
 * из которого может происходить смотрение/воспроизведение.
 * Нужно, чтобы понимать из какой части приложения воспроизводится контент.
 */
export enum TelemetryContentPlace {
    /**
     * Воспроизводится сериал. Универсальное значение плеера.
     */
    Series = 'player-screen',
    /**
     * Воспроизводится фильм. Универсальное значение плеера.
     */
    Film = 'player-screen',
    /**
     * Воспроизводится трейлер.
     */
    Trailer = 'trailer-screen',
    /**
     * Воспроизводится канал.
     */
    Channel = 'tv-player-screen',
    /**
     * Воспроизводится спортивный канал.
     */
    SportChannel = 'sport-player-screen',
    /**
     * Воспроизводится контент в промоблоке или подборке видео на витрине.
     */
    MainScreen = 'main-screen',
    /**
     * Воспроизводится контент в спортивном промоблоке на витрине.
     */
    SportPromo = 'sport-main-screen',
    /**
     * Воспроизводится трейлер на карточке тайтла.
     */
    MovieScreen = 'movie-screen',
}

export enum TelemetryContentType {
    Trailer = 'trailer',
    Channel = 'channel',
    SportLive = 'sport-live',
    Film = 'movie',
    Episode = 'episode',
    Clip = 'clip',
}
